import Auth from "@aws-amplify/auth";
import { Hub } from '@aws-amplify/core';
import { config } from "../config/config";
import constants from "../config/constants";

export let username = "";
let sessionInterval: NodeJS.Timeout | null = null;

/**
 * Redirects to the Cognito login page
 */
function redirectToLoginPage() {
  console.log('[Auth] redirectToLoginPage - Starting redirect to Cognito login');
  console.log('[Auth] Config:', (config as any).AMPLIFY_CONFIG);
  
  Auth.configure((config as any).AMPLIFY_CONFIG);
  console.log('[Auth] Amplify configured, calling federatedSignIn with AmazonFederate');
  
  // Use any type to bypass TypeScript check for custom Midway provider
  (Auth.federatedSignIn as any)({
    provider: "AmazonFederate",
    customState: window.location.href,
  });
  
  console.log('[Auth] federatedSignIn called');
}

/**
 * Starts the session timer
 */
async function startSessionTimer() {
  console.log('[Auth] startSessionTimer - Starting session timer');
  
  const fiveMinutes = 60 * 5;
  const twentyHour = 43200;
  const refreshTokenValidity = (config as any).REFRESH_TOKEN_VALIDITY * twentyHour;
  let sessionWillExpireNotice = false;

  try {
    const currentSession = await Auth.currentSession();
    console.log('[Auth] Current session retrieved');
    
    const accessToken = currentSession.getAccessToken();
    const expiresTimestamp = accessToken.getIssuedAt() + refreshTokenValidity;
    console.log('[Auth] Session expires at:', new Date(expiresTimestamp * 1000).toISOString());

    sessionInterval = setInterval(() => {
      const currentTimestamp = new Date().getTime() / 1000;
      const timeRemaining = expiresTimestamp - currentTimestamp;

      if (!sessionWillExpireNotice && timeRemaining < fiveMinutes) {
        sessionWillExpireNotice = true;
        console.log('[Auth] Session will expire in less than 5 minutes');
        Hub.dispatch(constants.SESSION_WILL_EXPIRE, {
          event: constants.SESSION_WILL_EXPIRE,
        });
      } else if (timeRemaining <= 0) {
        console.log('[Auth] Session expired');
        Hub.dispatch(constants.SESSION_EXPIRED, {
          event: constants.SESSION_EXPIRED,
        });
      }
    }, constants.SESSION_TIMER_INTERVAL);
    
    console.log('[Auth] Session timer started');
  } catch (e) {
    console.error('[Auth] startSessionTimer - Error:', e);
    Hub.dispatch(constants.SESSION_EXPIRED, {
      event: constants.SESSION_EXPIRED,
    });
  }
}

/**
 * Retrieves the JWT token for the currently authenticated user
 */
async function getAccessToken(): Promise<string> {
  let accessToken = "";

  try {
    const user = await Auth.currentAuthenticatedUser();
    accessToken = user.signInUserSession.idToken.jwtToken;
  } catch (ex) {
    console.error("[Auth] getAccessToken - Failed to get access token:", ex);
  }

  return accessToken;
}

/**
 * Checks if user has access tokens
 */
async function hasAccessTokens(): Promise<boolean> {
  try {
    await Auth.currentAuthenticatedUser();
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Initializes the session
 */
async function initializeSession(
  onSessionExpired: () => void = () => {},
  onSessionWillExpire: (willExpire: boolean) => void = () => {},
  onUserProfileChange: (user: any) => void = () => {}
) {
  try {
    const user = await Auth.currentAuthenticatedUser();
    onUserProfileChange(user);

    Hub.listen(constants.SESSION_EXPIRED, () => {
      if (sessionInterval) {
        clearInterval(sessionInterval);
      }
      signOut();
      onSessionExpired();
    });

    Hub.listen(constants.SESSION_WILL_EXPIRE, () => {
      onSessionWillExpire(true);
    });

    setTimeout(startSessionTimer, constants.SESSION_TIMER_INTERVAL);
  } catch (error) {
    console.error('[Auth] initializeSession - Error:', error);
    throw error;
  }
}

/**
 * Signs the user out
 */
async function signOut() {
  console.log('[Auth] signOut - Signing user out');
  
  try {
    await Auth.signOut();
    console.log('[Auth] User signed out successfully');
  } catch (error) {
    console.error('[Auth] signOut - Error:', error);
  }
}

/**
 * Gets user information including username from Midway, plus tokens and expiration
 * Using .then() pattern like working LensFactoryUI implementation
 */
async function getUserInfo(): Promise<any> {
  let userInfo: any = null;

  try {
    // Use .then() pattern like LensFactoryUI (working implementation)
    await Auth.currentAuthenticatedUser()
      .then(user => {
        const session = user.getSignInUserSession();
        const idToken = session.getIdToken();
        const accessToken = session.getAccessToken();
        const refreshToken = session.getRefreshToken();
        
        // Decode ID token payload
        const payload = idToken.decodePayload();
        
        // Combine payload with token information
        userInfo = {
          ...payload,
          idToken: idToken.getJwtToken(),
          accessToken: accessToken.getJwtToken(),
          refreshToken: refreshToken.getToken(),
          tokenExpiration: idToken.getExpiration(), // Unix timestamp in seconds
          authTime: payload.auth_time,
        };
        
        if (userInfo && userInfo.identities && userInfo.identities[0]) {
          username = userInfo.identities[0].userId; // Midway username
        }
      })
      .catch(err => {
        console.error('[Auth] Failed to get authenticated user:', err.message);
      });
  } catch (err) {
    console.error('[Auth] getUserInfo error:', err);
  }

  if (userInfo) {
    return new Promise(resolve => resolve(userInfo));
  }
  
  return null;
}

export {
  hasAccessTokens,
  initializeSession,
  getAccessToken,
  redirectToLoginPage,
  signOut,
  getUserInfo,
};
